import { c as _c } from "react/compiler-runtime";
// Модалка генерации артикула (Биржа товаров партнёрского кабинета).
//
// «Один артикул»: название ресурса/контента, площадка, кампания, канал ->
// плашка с кодом крупно, «Скопировать артикул» и готовая фраза для поста
// «Ищите товар на OSMIX по артикулу XXXXXXXX».
// «Массовая генерация» (только арбитражник): по строке на источник, до 50 ->
// POST /partner/articles/bulk -> таблица «код - источник», копирование всего
// пула CSV в буфер или скачивание файла.
import { UGC_BULK_MAX } from "@osmix/contracts/ugc-articles";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, type UgcArticle, type UgcCatalogItem, type UgcChannel, type UgcPartnerRole } from "@/api";
import { XIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { Textarea } from "@/components/ui/textarea";
import { articlesCsv, CodeChip, downloadCsv, partnerKey, UGC_CHANNELS, useCopy } from "./partner-ui";
type Mode = "single" | "bulk";
export function GenerateDialog(t0) {
  const $ = _c(29);
  const {
    item,
    role,
    userId,
    onOpenChange
  } = t0;
  const {
    t
  } = useTranslation();
  const [mode, setMode] = useState("single");
  const bulkAllowed = role === "arbitrage";
  const activeMode = bulkAllowed ? mode : "single";
  const t1 = Boolean(item);
  let t2;
  if ($[0] !== t) {
    t2 = t("partner.generate.title");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <DialogTitle className="min-w-0 flex-1 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-ds-ink">{t2}</DialogTitle>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("common.close");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <XIcon />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <DialogClose aria-label={t4} className="grid size-6 shrink-0 place-items-center rounded-[4px] text-ds-primary outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">{t5}</DialogClose>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <div className="flex items-start gap-2.5 px-4 pt-4 pb-2.5 md:px-5">{t3}{t6}</div>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = item?.productTitle;
  let t9;
  if ($[12] !== t8) {
    t9 = <DialogDescription className="sr-only">{t8}</DialogDescription>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== activeMode || $[15] !== bulkAllowed || $[16] !== item || $[17] !== onOpenChange || $[18] !== t || $[19] !== userId) {
    t10 = item && <div className="flex flex-col gap-4 px-4 pb-5 md:px-5"><div className="flex items-center gap-3 rounded-[16px] bg-ds-quaternary p-2"><div className="size-14 shrink-0 overflow-hidden rounded-[12px] bg-white">{item.productImage && <img src={item.productImage} alt="" className="size-full object-cover" />}</div><div className="flex min-w-0 flex-col gap-0.5"><p className="line-clamp-2 text-[14px] leading-[18px] font-semibold text-ds-ink">{item.productTitle}</p><p className="text-[12px] leading-4 text-ds-secondary">{item.shopName} · {t("partner.catalog.reward")} {item.commissionPercent} %</p></div></div>{bulkAllowed && <Segmented block={true} value={activeMode} onValueChange={setMode} options={[{
        value: "single",
        label: t("partner.generate.single")
      }, {
        value: "bulk",
        label: t("partner.generate.bulk")
      }]} />}{activeMode === "single" ? <SingleForm key={`s-${item.productId}`} item={item} userId={userId} onClose={() => onOpenChange(false)} /> : <BulkForm key={`b-${item.productId}`} item={item} userId={userId} />}</div>;
    $[14] = activeMode;
    $[15] = bulkAllowed;
    $[16] = item;
    $[17] = onOpenChange;
    $[18] = t;
    $[19] = userId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7 || $[23] !== t9) {
    t11 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[3px]" className="max-h-[92dvh] w-[calc(100vw-32px)] gap-0 overflow-y-auto rounded-[20px] border-0 bg-white p-0 shadow-none sm:max-w-[520px]" data-testid="partner-generate-dialog">{t7}{t9}{t10}</DialogContent>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== onOpenChange || $[26] !== t1 || $[27] !== t11) {
    t12 = <Dialog open={t1} onOpenChange={onOpenChange}>{t11}</Dialog>;
    $[25] = onOpenChange;
    $[26] = t1;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
function ChannelSelect(t0) {
  const $ = _c(23);
  const {
    value,
    onChange
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("partner.generate.channel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="text-[12px] leading-4 font-medium text-ds-label">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onChange) {
    t3 = v => onChange(v as UgcChannel);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("partner.generate.channel");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SelectValue />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <SelectTrigger className="w-full" aria-label={t4}>{t5}</SelectTrigger>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = UGC_CHANNELS.map(c => <SelectItem key={c} value={c}>{t(`partner.channel.${c}`)}</SelectItem>);
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <SelectContent>{t7}</SelectContent>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t3 || $[16] !== t6 || $[17] !== t8 || $[18] !== value) {
    t9 = <Select value={value} onValueChange={t3}>{t6}{t8}</Select>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t8;
    $[18] = value;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t2 || $[21] !== t9) {
    t10 = <div className="flex flex-col gap-1.5">{t2}{t9}</div>;
    $[20] = t2;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
const useInvalidate = userId => {
  const $ = _c(3);
  const qc = useQueryClient();
  let t0;
  if ($[0] !== qc || $[1] !== userId) {
    t0 = () => {
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "articles")
      });
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "catalog")
      });
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "stats")
      });
    };
    $[0] = qc;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function SingleForm(t0) {
  const $ = _c(118);
  const {
    item,
    userId,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const copy = useCopy();
  const invalidate = useInvalidate(userId);
  const [name, setName] = useState("");
  const [placement, setPlacement] = useState("");
  const [campaign, setCampaign] = useState("");
  const [channel, setChannel] = useState("telegram");
  const [touched, setTouched] = useState(false);
  const [created, setCreated] = useState(null);
  let t1;
  if ($[0] !== campaign || $[1] !== channel || $[2] !== item || $[3] !== name || $[4] !== placement) {
    t1 = () => api.ugc.partner.createArticle({
      productId: item.productId,
      name: name.trim(),
      placement: placement.trim(),
      campaign: campaign.trim(),
      channel
    });
    $[0] = campaign;
    $[1] = channel;
    $[2] = item;
    $[3] = name;
    $[4] = placement;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== invalidate) {
    t2 = article => {
      setCreated(article);
      invalidate();
    };
    $[6] = invalidate;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const create = useMutation(t4);
  if (created) {
    let t5;
    if ($[14] !== created.code || $[15] !== t) {
      t5 = t("partner.generate.phrase", {
        code: created.code
      });
      $[14] = created.code;
      $[15] = t;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    const phrase = t5;
    let t6;
    if ($[17] !== t) {
      t6 = t("partner.generate.code");
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t6) {
      t7 = <span className="text-[12px] leading-4 font-medium text-ds-label">{t6}</span>;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== created.code) {
      t8 = <span className="font-mono text-[36px] leading-[40px] font-bold tracking-[0.12em] text-ds-ink sm:text-[44px] sm:leading-[48px]" data-testid="partner-generated-code">{created.code}</span>;
      $[21] = created.code;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== copy || $[24] !== created.code) {
      t9 = () => void copy(created.code);
      $[23] = copy;
      $[24] = created.code;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Copy />;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t) {
      t11 = t("partner.generate.copyCode");
      $[27] = t;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11 || $[30] !== t9) {
      t12 = <Button size="md" onClick={t9} data-testid="partner-copy-code">{t10}{t11}</Button>;
      $[29] = t11;
      $[30] = t9;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== t12 || $[33] !== t7 || $[34] !== t8) {
      t13 = <div className="flex flex-col items-center gap-3 rounded-[20px] bg-ds-quaternary px-4 py-6 text-center">{t7}{t8}{t12}</div>;
      $[32] = t12;
      $[33] = t7;
      $[34] = t8;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== phrase) {
      t14 = <p className="text-[14px] leading-[18px] font-medium text-ds-primary">«{phrase}»</p>;
      $[36] = phrase;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t15;
    if ($[38] !== copy || $[39] !== phrase) {
      t15 = () => void copy(phrase);
      $[38] = copy;
      $[39] = phrase;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    let t16;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <Copy />;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== t) {
      t17 = t("partner.generate.copyPhrase");
      $[42] = t;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] !== t15 || $[45] !== t17) {
      t18 = <Button variant="gray" size="sm" className="self-start" onClick={t15}>{t16}{t17}</Button>;
      $[44] = t15;
      $[45] = t17;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== t14 || $[48] !== t18) {
      t19 = <div className="flex flex-col gap-2 rounded-[16px] border border-ds-line-2 p-3">{t14}{t18}</div>;
      $[47] = t14;
      $[48] = t18;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    let t20;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = () => {
        setCreated(null);
        setName("");
        setTouched(false);
      };
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    let t21;
    if ($[51] !== t) {
      t21 = t("partner.generate.another");
      $[51] = t;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] !== t21) {
      t22 = <Button variant="gray" size="md" onClick={t20}>{t21}</Button>;
      $[53] = t21;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] !== t) {
      t23 = t("partner.generate.done");
      $[55] = t;
      $[56] = t23;
    } else {
      t23 = $[56];
    }
    let t24;
    if ($[57] !== onClose || $[58] !== t23) {
      t24 = <Button size="md" onClick={onClose}>{t23}</Button>;
      $[57] = onClose;
      $[58] = t23;
      $[59] = t24;
    } else {
      t24 = $[59];
    }
    let t25;
    if ($[60] !== t22 || $[61] !== t24) {
      t25 = <div className="flex flex-wrap gap-2">{t22}{t24}</div>;
      $[60] = t22;
      $[61] = t24;
      $[62] = t25;
    } else {
      t25 = $[62];
    }
    let t26;
    if ($[63] !== t13 || $[64] !== t19 || $[65] !== t25) {
      t26 = <div className="flex flex-col gap-4" data-testid="partner-generated">{t13}{t19}{t25}</div>;
      $[63] = t13;
      $[64] = t19;
      $[65] = t25;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    return t26;
  }
  let t5;
  if ($[67] !== create || $[68] !== name) {
    t5 = e_0 => {
      e_0.preventDefault();
      setTouched(true);
      if (!name.trim() || create.isPending) {
        return;
      }
      create.mutate();
    };
    $[67] = create;
    $[68] = name;
    $[69] = t5;
  } else {
    t5 = $[69];
  }
  let t6;
  if ($[70] !== t) {
    t6 = t("partner.generate.name");
    $[70] = t;
    $[71] = t6;
  } else {
    t6 = $[71];
  }
  let t7;
  if ($[72] !== t) {
    t7 = t("partner.generate.namePlaceholder");
    $[72] = t;
    $[73] = t7;
  } else {
    t7 = $[73];
  }
  let t8;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_1 => setName(e_1.target.value);
    $[74] = t8;
  } else {
    t8 = $[74];
  }
  let t9;
  if ($[75] !== name || $[76] !== t || $[77] !== touched) {
    t9 = touched && !name.trim() ? t("partner.generate.nameRequired") : undefined;
    $[75] = name;
    $[76] = t;
    $[77] = touched;
    $[78] = t9;
  } else {
    t9 = $[78];
  }
  let t10;
  if ($[79] !== name || $[80] !== t6 || $[81] !== t7 || $[82] !== t9) {
    t10 = <TextField label={t6} placeholder={t7} value={name} maxLength={120} onChange={t8} error={t9} data-testid="partner-generate-name" />;
    $[79] = name;
    $[80] = t6;
    $[81] = t7;
    $[82] = t9;
    $[83] = t10;
  } else {
    t10 = $[83];
  }
  let t11;
  if ($[84] !== t) {
    t11 = t("partner.generate.placement");
    $[84] = t;
    $[85] = t11;
  } else {
    t11 = $[85];
  }
  let t12;
  if ($[86] !== t) {
    t12 = t("partner.generate.placementPlaceholder");
    $[86] = t;
    $[87] = t12;
  } else {
    t12 = $[87];
  }
  let t13;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_2 => setPlacement(e_2.target.value);
    $[88] = t13;
  } else {
    t13 = $[88];
  }
  let t14;
  if ($[89] !== placement || $[90] !== t11 || $[91] !== t12) {
    t14 = <TextField label={t11} placeholder={t12} value={placement} maxLength={160} onChange={t13} />;
    $[89] = placement;
    $[90] = t11;
    $[91] = t12;
    $[92] = t14;
  } else {
    t14 = $[92];
  }
  let t15;
  if ($[93] !== t) {
    t15 = t("partner.generate.campaign");
    $[93] = t;
    $[94] = t15;
  } else {
    t15 = $[94];
  }
  let t16;
  if ($[95] !== t) {
    t16 = t("partner.generate.campaignPlaceholder");
    $[95] = t;
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_3 => setCampaign(e_3.target.value);
    $[97] = t17;
  } else {
    t17 = $[97];
  }
  let t18;
  if ($[98] !== campaign || $[99] !== t15 || $[100] !== t16) {
    t18 = <TextField label={t15} placeholder={t16} value={campaign} maxLength={160} onChange={t17} />;
    $[98] = campaign;
    $[99] = t15;
    $[100] = t16;
    $[101] = t18;
  } else {
    t18 = $[101];
  }
  let t19;
  if ($[102] !== channel) {
    t19 = <ChannelSelect value={channel} onChange={setChannel} />;
    $[102] = channel;
    $[103] = t19;
  } else {
    t19 = $[103];
  }
  let t20;
  if ($[104] !== t18 || $[105] !== t19) {
    t20 = <div className="grid gap-3 sm:grid-cols-2">{t18}{t19}</div>;
    $[104] = t18;
    $[105] = t19;
    $[106] = t20;
  } else {
    t20 = $[106];
  }
  const t21 = create.isPending;
  let t22;
  if ($[107] !== t) {
    t22 = t("partner.generate.submit");
    $[107] = t;
    $[108] = t22;
  } else {
    t22 = $[108];
  }
  let t23;
  if ($[109] !== create.isPending || $[110] !== t22) {
    t23 = <Button type="submit" size="lg" className="mt-1" disabled={t21} data-testid="partner-generate-submit">{t22}</Button>;
    $[109] = create.isPending;
    $[110] = t22;
    $[111] = t23;
  } else {
    t23 = $[111];
  }
  let t24;
  if ($[112] !== t10 || $[113] !== t14 || $[114] !== t20 || $[115] !== t23 || $[116] !== t5) {
    t24 = <form className="flex flex-col gap-3" onSubmit={t5}>{t10}{t14}{t20}{t23}</form>;
    $[112] = t10;
    $[113] = t14;
    $[114] = t20;
    $[115] = t23;
    $[116] = t5;
    $[117] = t24;
  } else {
    t24 = $[117];
  }
  return t24;
}
function BulkForm(t0) {
  const $ = _c(107);
  const {
    item,
    userId
  } = t0;
  const {
    t
  } = useTranslation();
  const copy = useCopy();
  const invalidate = useInvalidate(userId);
  const [text, setText] = useState("");
  const [campaign, setCampaign] = useState("");
  const [channel, setChannel] = useState("yandex_direct");
  const [created, setCreated] = useState(null);
  const sources = text.split("\n").map(_temp).filter(Boolean);
  const tooMany = sources.length > UGC_BULK_MAX;
  let t1;
  if ($[0] !== invalidate || $[1] !== t) {
    t1 = list => {
      setCreated(list);
      invalidate();
      toast.success(t("partner.generate.bulkDone", {
        count: list.length
      }));
    };
    $[0] = invalidate;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const bulk = useMutation({
    mutationFn: () => api.ugc.partner.bulk({
      productId: item.productId,
      sources: sources.map(name => ({
        name: name.slice(0, 120),
        campaign: campaign.trim() || undefined,
        channel
      }))
    }),
    onSuccess: t1,
    onError: t2
  });
  if (created) {
    let t3;
    if ($[5] !== created || $[6] !== t) {
      const header = [t("partner.articles.code"), t("partner.generate.source"), t("partner.generate.placement"), t("partner.generate.campaign"), t("partner.articles.product")];
      t3 = articlesCsv(created, header);
      $[5] = created;
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const csv = t3;
    let t4;
    if ($[8] !== created.length || $[9] !== t) {
      t4 = t("partner.generate.bulkDone", {
        count: created.length
      });
      $[8] = created.length;
      $[9] = t;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t4) {
      t5 = <p className="text-[14px] leading-[18px] font-semibold text-ds-ink">{t4}</p>;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t) {
      t6 = t("partner.articles.code");
      $[13] = t;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6) {
      t7 = <th className="px-3 py-2 font-medium">{t6}</th>;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== t) {
      t8 = t("partner.generate.source");
      $[17] = t;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t8) {
      t9 = <th className="px-3 py-2 font-medium">{t8}</th>;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== t7 || $[22] !== t9) {
      t10 = <thead className="sticky top-0 bg-ds-quaternary text-ds-label"><tr>{t7}{t9}</tr></thead>;
      $[21] = t7;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== created) {
      t11 = created.map(_temp2);
      $[24] = created;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== t11) {
      t12 = <tbody>{t11}</tbody>;
      $[26] = t11;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== t10 || $[29] !== t12) {
      t13 = <div className="max-h-[320px] overflow-auto rounded-[16px] border border-ds-line-2"><table className="w-full text-left text-[13px] leading-[17px]">{t10}{t12}</table></div>;
      $[28] = t10;
      $[29] = t12;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== copy || $[32] !== csv) {
      t14 = () => void copy(csv);
      $[31] = copy;
      $[32] = csv;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Copy />;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== t) {
      t16 = t("partner.generate.copyAll");
      $[35] = t;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    let t17;
    if ($[37] !== t14 || $[38] !== t16) {
      t17 = <Button size="md" onClick={t14} data-testid="partner-bulk-copy">{t15}{t16}</Button>;
      $[37] = t14;
      $[38] = t16;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== csv || $[41] !== item.productId) {
      t18 = () => downloadCsv(`osmix-articles-${item.productId}.csv`, csv);
      $[40] = csv;
      $[41] = item.productId;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Download />;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== t) {
      t20 = t("partner.generate.download");
      $[44] = t;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== t18 || $[47] !== t20) {
      t21 = <Button variant="gray" size="md" onClick={t18}>{t19}{t20}</Button>;
      $[46] = t18;
      $[47] = t20;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = () => setCreated(null);
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== t) {
      t23 = t("partner.generate.another");
      $[50] = t;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== t23) {
      t24 = <Button variant="ghost" size="md" onClick={t22}>{t23}</Button>;
      $[52] = t23;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    let t25;
    if ($[54] !== t17 || $[55] !== t21 || $[56] !== t24) {
      t25 = <div className="flex flex-wrap gap-2">{t17}{t21}{t24}</div>;
      $[54] = t17;
      $[55] = t21;
      $[56] = t24;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    let t26;
    if ($[58] !== t13 || $[59] !== t25 || $[60] !== t5) {
      t26 = <div className="flex flex-col gap-3" data-testid="partner-bulk-result">{t5}{t13}{t25}</div>;
      $[58] = t13;
      $[59] = t25;
      $[60] = t5;
      $[61] = t26;
    } else {
      t26 = $[61];
    }
    return t26;
  }
  const t3 = "flex flex-col gap-3";
  const t4 = e_0 => {
    e_0.preventDefault();
    if (sources.length === 0 || tooMany || bulk.isPending) {
      return;
    }
    bulk.mutate();
  };
  const t5 = "flex flex-col gap-1.5";
  const t6 = "flex items-baseline justify-between gap-2";
  let t7;
  if ($[62] !== t) {
    t7 = t("partner.generate.sources");
    $[62] = t;
    $[63] = t7;
  } else {
    t7 = $[63];
  }
  let t8;
  if ($[64] !== t7) {
    t8 = <label htmlFor="partner-bulk-sources" className="text-[12px] leading-4 font-medium text-ds-label">{t7}</label>;
    $[64] = t7;
    $[65] = t8;
  } else {
    t8 = $[65];
  }
  const t9 = tooMany ? "text-[12px] leading-4 text-ds-error" : "text-[12px] leading-4 text-ds-secondary";
  const t10 = t("partner.generate.sourcesCount", {
    count: sources.length,
    max: UGC_BULK_MAX
  });
  let t11;
  if ($[66] !== t10 || $[67] !== t9) {
    t11 = <span className={t9}>{t10}</span>;
    $[66] = t10;
    $[67] = t9;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== t11 || $[70] !== t8) {
    t12 = <div className={t6}>{t8}{t11}</div>;
    $[69] = t11;
    $[70] = t8;
    $[71] = t12;
  } else {
    t12 = $[71];
  }
  let t13;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_1 => setText(e_1.target.value);
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  const t14 = tooMany || undefined;
  let t15;
  if ($[73] !== t14 || $[74] !== text) {
    t15 = <Textarea id="partner-bulk-sources" rows={6} value={text} onChange={t13} placeholder={"Yandex_Direct_Camp_1\nTelegram_Ad_Net_2\nVK_Ads_Lookalike"} aria-invalid={t14} className="min-h-36 font-mono text-[13px] leading-[18px]" data-testid="partner-bulk-sources" />;
    $[73] = t14;
    $[74] = text;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  let t16;
  if ($[76] !== t || $[77] !== tooMany) {
    t16 = tooMany && <p className="text-[12px] leading-4 text-ds-error">{t("partner.generate.tooMany", {
        max: UGC_BULK_MAX
      })}</p>;
    $[76] = t;
    $[77] = tooMany;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  let t17;
  if ($[79] !== t12 || $[80] !== t15 || $[81] !== t16) {
    t17 = <div className={t5}>{t12}{t15}{t16}</div>;
    $[79] = t12;
    $[80] = t15;
    $[81] = t16;
    $[82] = t17;
  } else {
    t17 = $[82];
  }
  let t18;
  if ($[83] !== t) {
    t18 = t("partner.generate.campaign");
    $[83] = t;
    $[84] = t18;
  } else {
    t18 = $[84];
  }
  let t19;
  if ($[85] !== t) {
    t19 = t("partner.generate.campaignPlaceholder");
    $[85] = t;
    $[86] = t19;
  } else {
    t19 = $[86];
  }
  let t20;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_2 => setCampaign(e_2.target.value);
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== campaign || $[89] !== t18 || $[90] !== t19) {
    t21 = <TextField label={t18} placeholder={t19} value={campaign} maxLength={160} onChange={t20} />;
    $[88] = campaign;
    $[89] = t18;
    $[90] = t19;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== channel) {
    t22 = <ChannelSelect value={channel} onChange={setChannel} />;
    $[92] = channel;
    $[93] = t22;
  } else {
    t22 = $[93];
  }
  let t23;
  if ($[94] !== t21 || $[95] !== t22) {
    t23 = <div className="grid gap-3 sm:grid-cols-2">{t21}{t22}</div>;
    $[94] = t21;
    $[95] = t22;
    $[96] = t23;
  } else {
    t23 = $[96];
  }
  const t24 = sources.length === 0 || tooMany || bulk.isPending;
  let t25;
  if ($[97] !== t) {
    t25 = t("partner.generate.bulkSubmit");
    $[97] = t;
    $[98] = t25;
  } else {
    t25 = $[98];
  }
  let t26;
  if ($[99] !== t24 || $[100] !== t25) {
    t26 = <Button type="submit" size="lg" className="mt-1" disabled={t24} data-testid="partner-bulk-submit">{t25}</Button>;
    $[99] = t24;
    $[100] = t25;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  let t27;
  if ($[102] !== t17 || $[103] !== t23 || $[104] !== t26 || $[105] !== t4) {
    t27 = <form className={t3} onSubmit={t4}>{t17}{t23}{t26}</form>;
    $[102] = t17;
    $[103] = t23;
    $[104] = t26;
    $[105] = t4;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  return t27;
}
function _temp2(a) {
  return <tr key={a.id} className="border-t border-ds-line-2"><td className="px-3 py-1.5"><CodeChip code={a.code} /></td><td className="px-3 py-1.5 break-all text-ds-primary">{a.name}</td></tr>;
}
function _temp(line) {
  return line.trim();
}